import React, { useState, useEffect, useRef, useCallback } from 'react';
import { sanitizeUserFacingError } from '../utils/userFacingError';
import { getReferralSignupPayload } from '../utils/referralTracking';
import { getSessionId } from '../utils/sessionTracker';
import './LoginModal.css';

function getPasswordStrength(password) {
  const pwd = password || '';
  if (pwd.length === 0) return { label: '', level: 0 };
  if (pwd.length < 6) return { label: 'Too short (min. 6 characters)', level: 0 };
  let score = 0;
  if (pwd.length >= 8) score += 1;
  if (pwd.length >= 12) score += 1;
  if (/[a-z]/.test(pwd) && /[A-Z]/.test(pwd)) score += 1;
  if (/\d/.test(pwd)) score += 1;
  if (/[^a-zA-Z0-9]/.test(pwd)) score += 1;
  if (score <= 1) return { label: 'Weak', level: 1 };
  if (score === 2) return { label: 'Fair', level: 2 };
  if (score <= 3) return { label: 'Good', level: 3 };
  return { label: 'Strong', level: 4 };
}

function formatApiError(errorData) {
  if (!errorData || typeof errorData !== 'object') {
    return 'Something went wrong. Please try again.';
  }
  let message = 'Something went wrong. Please try again.';
  if (Array.isArray(errorData.errors) && errorData.errors.length > 0) {
    message = errorData.errors
      .map((e) => e.msg || e.message || String(e))
      .filter(Boolean)
      .join(' ');
  } else if (typeof errorData.message === 'string' && errorData.message) {
    message = errorData.message;
  }
  return sanitizeUserFacingError(message, 'Something went wrong. Please try again.');
}

const LoginModal = ({ isOpen, onClose, onLogin, isLoggedIn = false, userInfo = null }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [loginMethod, setLoginMethod] = useState('email');
  const [rememberMe, setRememberMe] = useState(true);
  const [marketingConsent, setMarketingConsent] = useState(true);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordMessage, setForgotPasswordMessage] = useState('');
  const [formError, setFormError] = useState('');
  const [oauthError, setOauthError] = useState('');
  const [authSubmitting, setAuthSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const googleButtonRef = useRef(null);
  const rememberMeRef = useRef(rememberMe);
  const previousActiveElementRef = useRef(null);
  const emailInputRef = useRef(null);
  const phoneSignUpRef = useRef(null);
  const phoneLoginRef = useRef(null);
  const modalRef = useRef(null);

  const passwordStrength = isSignUp ? getPasswordStrength(password) : { label: '', level: 0 };

  useEffect(() => {
    rememberMeRef.current = rememberMe;
  }, [rememberMe]);

  const clearMessages = useCallback(() => {
    setFormError('');
    setOauthError('');
  }, []);

  const closeModal = useCallback(() => {
    onClose();
    window.setTimeout(() => {
      const el = previousActiveElementRef.current;
      if (el && typeof el.focus === 'function') {
        el.focus();
      }
    }, 0);
  }, [onClose]);

  useEffect(() => {
    if (isOpen && isLoggedIn && userInfo) {
      setEmail(userInfo.email || '');
    }
  }, [isOpen, isLoggedIn, userInfo]);

  useEffect(() => {
    if (!isOpen) return;
    previousActiveElementRef.current = document.activeElement;
    const t = window.setTimeout(() => {
      if (isSignUp) {
        phoneSignUpRef.current?.focus();
      } else if (loginMethod === 'email') {
        emailInputRef.current?.focus();
      } else {
        phoneLoginRef.current?.focus();
      }
    }, 50);
    return () => clearTimeout(t);
  }, [isOpen, isSignUp, loginMethod]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      if (showForgotPassword) {
        setShowForgotPassword(false);
        setForgotPasswordEmail('');
        setForgotPasswordMessage('');
      } else {
        closeModal();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, showForgotPassword, closeModal]);

  useEffect(() => {
    if (!isOpen) return;

    const runGoogleInit = () => initializeGoogleSignIn();

    if (!window.google) {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = runGoogleInit;
      document.body.appendChild(script);
    } else {
      runGoogleInit();
    }
  }, [isOpen, isSignUp]);

  const initializeGoogleSignIn = () => {
    if (window.google && googleButtonRef.current) {
      const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

      if (!clientId || clientId === 'YOUR_GOOGLE_CLIENT_ID') {
        if (googleButtonRef.current) {
          googleButtonRef.current.innerHTML = `
            <div style="padding: 12px; background: #fff3cd; border: 1px solid #ffc107; border-radius: 6px; color: #856404; font-size: 13px; text-align: center;">
              Google Sign-In requires a Client ID. Please configure REACT_APP_GOOGLE_CLIENT_ID in your .env file.
            </div>
          `;
        }
        return;
      }

      if (googleButtonRef.current) {
        googleButtonRef.current.innerHTML = '';
      }

      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: handleGoogleSignIn,
        auto_select: true,
        cancel_on_tap_outside: true,
        ux_mode: 'popup',
      });

      window.google.accounts.id.renderButton(googleButtonRef.current, {
        theme: 'outline',
        size: 'large',
        text: isSignUp ? 'signup_with' : 'signin_with',
        shape: 'rectangular',
        width: 320,
        locale: 'en',
      });
    }
  };

  const handleGoogleSignIn = async (response) => {
    setOauthError('');
    try {
      const payload = JSON.parse(atob(response.credential.split('.')[1]));
      const userEmail = payload.email;
      const userName = payload.name;
      const userPicture = payload.picture;
      const googleId = payload.sub;

      const API_URL = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');

      const backendResponse = await fetch(`${API_URL}/api/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: userEmail,
          name: userName,
          googleId,
          picture: userPicture,
        }),
      });

      if (!backendResponse.ok) {
        const errorData = await backendResponse.json().catch(() => ({}));
        throw new Error(formatApiError(errorData) || 'Failed to authenticate with Google');
      }

      const data = await backendResponse.json();

      if (onLogin) {
        onLogin({
          email: userEmail,
          name: userName,
          picture: userPicture,
          method: 'google',
          token: data.token,
          id: data.user.id || data.user._id,
          userId: data.user.id || data.user._id,
          googleSurveyConsent: Boolean(data.user?.googleSurveyConsent),
          marketingConsent: Boolean(data.user?.marketingConsent),
          rememberMe: rememberMeRef.current,
        });
      }
      closeModal();
    } catch (error) {
      console.error('Error processing Google sign-in:', error);
      setOauthError(sanitizeUserFacingError(error.message, 'Error signing in with Google. Please try again.'));
    }
  };

  const validatePhone = (phoneNumber) => {
    const digitsOnly = phoneNumber.replace(/\D/g, '');
    return digitsOnly.length === 10;
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 10) {
      setPhone(value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearMessages();

    if (!isSignUp && loginMethod === 'phone') {
      if (!phone || !validatePhone(phone)) {
        setFormError('Please enter a valid 10-digit phone number.');
        return;
      }
    }

    if (isSignUp && password.length < 6) {
      setFormError('Password must be at least 6 characters.');
      return;
    }

    if (isSignUp) {
      try {
        const API_URL = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');

        const emailCheck = await fetch(`${API_URL}/api/auth/check-email?email=${encodeURIComponent(email)}`);
        if (emailCheck.ok) {
          const emailData = await emailCheck.json();
          if (emailData.exists) {
            setFormError('An account with this email already exists. Switch to Log in.');
            setIsSignUp(false);
            return;
          }
        }

        if (phone) {
          const phoneCheck = await fetch(`${API_URL}/api/auth/check-phone?phone=${encodeURIComponent(phone)}`);
          if (phoneCheck.ok) {
            const phoneData = await phoneCheck.json();
            if (phoneData.exists) {
              setFormError('This phone number is already registered. Please log in instead.');
              return;
            }
          }
        }
      } catch (err) {
        console.error('Error checking existing accounts:', err);
      }
    }

    setAuthSubmitting(true);
    try {
      const API_URL = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');
      let response;

      if (isSignUp) {
        response = await fetch(`${API_URL}/api/auth/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email,
            password,
            name: email.split('@')[0],
            phone: phone || undefined,
            marketingConsent,
            ...getReferralSignupPayload(getSessionId()),
          }),
        });
      } else if (loginMethod === 'phone') {
        response = await fetch(`${API_URL}/api/auth/login-phone`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            phone: phone.replace(/\D/g, ''),
            password,
          }),
        });
      } else {
        response = await fetch(`${API_URL}/api/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password }),
        });
      }

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        setFormError(formatApiError(data));
        return;
      }

      if (onLogin) {
        onLogin({
          email: data.user.email || email,
          name: data.user.name || email.split('@')[0],
          phone: data.user.phone || phone,
          method: loginMethod === 'phone' ? 'phone' : 'email',
          token: data.token,
          id: data.user.id || data.user._id,
          userId: data.user.id || data.user._id,
          googleSurveyConsent: Boolean(data.user?.googleSurveyConsent),
          marketingConsent: Boolean(data.user?.marketingConsent),
          rememberMe,
        });
      }
      closeModal();
      setEmail('');
      setPassword('');
      setPhone('');
      setLoginMethod('email');
    } catch (error) {
      console.error('Authentication error:', error);
      setFormError(sanitizeUserFacingError(error.message, 'Authentication failed. Please try again.'));
    } finally {
      setAuthSubmitting(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotPasswordEmail || !forgotPasswordEmail.includes('@')) {
      setForgotPasswordMessage('Please enter a valid email address');
      return;
    }

    setForgotPasswordLoading(true);
    setForgotPasswordMessage('');

    try {
      const API_URL = process.env.REACT_APP_API_URL?.replace(/\/+$/, '') || '';
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotPasswordEmail }),
      });

      const data = await response.json();

      if (response.ok) {
        setForgotPasswordMessage('Password reset instructions have been sent to your email address.');
        setTimeout(() => {
          setShowForgotPassword(false);
          setForgotPasswordEmail('');
          setForgotPasswordMessage('');
        }, 3000);
      } else {
        setForgotPasswordMessage(formatApiError(data));
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      setForgotPasswordMessage('An error occurred. Please try again later.');
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="login-overlay" onClick={closeModal} role="presentation">
      <div
        ref={modalRef}
        className="login-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="login-modal-title"
      >
        <button type="button" className="close-login" onClick={closeModal} aria-label="Close">
          ×
        </button>
        <div className="login-header">
          {isLoggedIn && userInfo && (
            <div className="logged-in-user-info">
              {userInfo.picture && (
                <img src={userInfo.picture} alt={userInfo.name || 'User'} className="user-picture" />
              )}
              <div className="user-details">
                <h3>{userInfo.name || 'User'}</h3>
                <p>{userInfo.email}</p>
              </div>
            </div>
          )}
          <h2 id="login-modal-title">{isSignUp ? 'Sign Up' : 'Log In'}</h2>
          <p>{isSignUp ? 'Create a new account' : isLoggedIn ? 'You are logged in' : 'Welcome back to Raheasy'}</p>
        </div>

        {(formError || oauthError) && (
          <div className="login-form-error" role="alert">
            {formError || oauthError}
          </div>
        )}

        <form className="login-form" onSubmit={handleSubmit} noValidate>
          {!isSignUp && (
            <div className="login-method-selector" role="group" aria-label="Login with email or phone">
              <button
                type="button"
                className={`method-btn ${loginMethod === 'email' ? 'active' : ''}`}
                onClick={() => {
                  setLoginMethod('email');
                  setPhone('');
                  clearMessages();
                }}
                aria-pressed={loginMethod === 'email'}
              >
                Email
              </button>
              <button
                type="button"
                className={`method-btn ${loginMethod === 'phone' ? 'active' : ''}`}
                onClick={() => {
                  setLoginMethod('phone');
                  setEmail('');
                  clearMessages();
                }}
                aria-pressed={loginMethod === 'phone'}
              >
                Phone
              </button>
            </div>
          )}
          {isSignUp && (
            <div className="form-group">
              <label htmlFor="phone">Phone Number (10 digits)</label>
              <input
                ref={phoneSignUpRef}
                type="tel"
                id="phone"
                value={phone}
                onChange={(e) => {
                  handlePhoneChange(e);
                  clearMessages();
                }}
                placeholder="Enter 10-digit phone number"
                maxLength={10}
                required={isSignUp}
                disabled={authSubmitting}
                autoComplete="tel"
              />
            </div>
          )}
          {loginMethod === 'email' || isSignUp ? (
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                ref={emailInputRef}
                type="email"
                id="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  clearMessages();
                }}
                placeholder="Enter your email"
                required={loginMethod === 'email' || isSignUp}
                disabled={(isLoggedIn && userInfo) || authSubmitting}
                autoComplete="email"
              />
            </div>
          ) : (
            <div className="form-group">
              <label htmlFor="phone-login">Phone Number (10 digits)</label>
              <input
                ref={phoneLoginRef}
                type="tel"
                id="phone-login"
                value={phone}
                onChange={(e) => {
                  handlePhoneChange(e);
                  clearMessages();
                }}
                placeholder="Enter 10-digit phone number"
                maxLength={10}
                required
                disabled={authSubmitting}
                autoComplete="tel"
              />
            </div>
          )}
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="password-input-wrap">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  clearMessages();
                }}
                placeholder={isSignUp ? 'Create a password' : 'Enter your password'}
                required={!isLoggedIn || !userInfo}
                disabled={authSubmitting}
                autoComplete={isSignUp ? 'new-password' : 'current-password'}
                aria-describedby={isSignUp && password ? 'password-strength-text' : undefined}
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={() => setShowPassword((v) => !v)}
                aria-pressed={showPassword}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                disabled={authSubmitting}
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
            {isSignUp && password.length > 0 && (
              <p
                id="password-strength-text"
                className={`password-strength password-strength-${passwordStrength.level}`}
              >
                {passwordStrength.label}
              </p>
            )}
          </div>
          {isSignUp && (
            <div className="form-options form-options-remember-only">
              <label className="remember-me">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  disabled={authSubmitting}
                />
                <span>Keep me signed in on this device</span>
              </label>
              <label className="remember-me">
                <input
                  type="checkbox"
                  checked={marketingConsent}
                  onChange={(e) => setMarketingConsent(e.target.checked)}
                  disabled={authSubmitting}
                />
                <span>Email me deals, updates, and promotions</span>
              </label>
            </div>
          )}
          {!isSignUp && (
            <div className="form-options">
              <label className="remember-me">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  disabled={authSubmitting}
                />
                <span>Remember me</span>
              </label>
              <button
                type="button"
                className="forgot-password"
                onClick={(e) => {
                  e.preventDefault();
                  setShowForgotPassword(true);
                }}
                disabled={authSubmitting}
              >
                Forgot password?
              </button>
            </div>
          )}
          <button type="submit" className="login-submit-btn" disabled={authSubmitting}>
            {authSubmitting ? (isSignUp ? 'Creating account…' : 'Signing in…') : isSignUp ? 'Sign Up' : 'Log In'}
          </button>
        </form>

        <div className="social-login-divider">
          <span>or continue with</span>
        </div>

        <div className="oauth-providers" role="group" aria-label="Sign in with a social account">
          <div className="oauth-provider-slot oauth-provider-google">
            <div ref={googleButtonRef} className="google-signin-button" />
          </div>
        </div>

        <p className="oauth-remember-hint">
          {isSignUp
            ? 'Google uses the same “Keep me signed in” option as email sign-up.'
            : 'Google uses the same “Remember me” option as email login.'}
        </p>

        <div className="login-footer">
          <p>
            {isSignUp ? 'Already have an account? ' : "Don't have an account? "}
            <button
              type="button"
              className="toggle-auth"
              onClick={() => {
                setIsSignUp(!isSignUp);
                clearMessages();
              }}
              disabled={authSubmitting}
            >
              {isSignUp ? 'Log In' : 'Sign Up'}
            </button>
          </p>
        </div>

        {showForgotPassword && (
          <div className="forgot-password-modal">
            <div className="forgot-password-content">
              <button
                type="button"
                className="close-forgot-password"
                onClick={() => {
                  setShowForgotPassword(false);
                  setForgotPasswordEmail('');
                  setForgotPasswordMessage('');
                }}
                aria-label="Close reset password"
              >
                ×
              </button>
              <h3>Reset Password</h3>
              <p>Enter your email address and we&apos;ll send you instructions to reset your password.</p>
              <form onSubmit={handleForgotPassword}>
                <div className="form-group">
                  <label htmlFor="forgot-email">Email Address</label>
                  <input
                    type="email"
                    id="forgot-email"
                    value={forgotPasswordEmail}
                    onChange={(e) => setForgotPasswordEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                    disabled={forgotPasswordLoading}
                    autoComplete="email"
                  />
                </div>
                {forgotPasswordMessage && (
                  <div
                    className={`forgot-password-message ${
                      forgotPasswordMessage.includes('sent') ? 'success' : 'error'
                    }`}
                  >
                    {forgotPasswordMessage}
                  </div>
                )}
                <button type="submit" className="login-submit-btn" disabled={forgotPasswordLoading}>
                  {forgotPasswordLoading ? 'Sending...' : 'Send Reset Instructions'}
                </button>
                <button
                  type="button"
                  className="cancel-forgot-password"
                  onClick={() => {
                    setShowForgotPassword(false);
                    setForgotPasswordEmail('');
                    setForgotPasswordMessage('');
                  }}
                  disabled={forgotPasswordLoading}
                >
                  Cancel
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoginModal;
